import React, { useState } from 'react';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import {
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import Icon from 'dpl/common/components/Icon';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import IconSet from 'dpl/breeder_dashboard_v2/components/litters/IconSet';
import { HELP_CENTER_ADULT_DOGS_PATH } from 'dpl/shared/constants/urls';
import SmartLink from 'dpl/components/SmartLink';

import { usePuppyData } from '../hooks/usePuppyData';
import Section from './Section';
import PriceBreakdown from './PriceBreakdown';
import BuyNowPayLaterDetails from './BuyNowPayLaterDetails';

const IS_LT_MD = isLessThanBreakpoint('md');

const BADGE_DIMS = IS_LT_MD
  ? { width: '20px', height: '22px' }
  : { width: '28px', height: '32px' };

export default function AboutPuppy() {
  const { puppy, breeder, includedWithPuppy, ownerStories } = usePuppyData();
  const [showFullList, setShowFullList] = useState(false);

  function getBreederDescription() {
    const hasClubMembership = breeder.club_count > 0;
    const isPreferredBreeder = breeder.is_preferred_breeder;

    const clubMembership = hasClubMembership
      ? `${breeder.club_count} club ${
          breeder.club_count > 1 ? 'memberships' : 'membership'
        }`
      : '';
    const preferredBreeder = isPreferredBreeder ? 'Preferred Breeder' : '';

    return (
      [clubMembership, preferredBreeder].filter(Boolean).join(' • ') ||
      'Certified Good Dog Breeder'
    );
  }

  const breederResponseDescription = [
    breeder.response_rate_description,
    breeder.response_time_description
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div className="AboutPuppy font-16 pt8 pt12-md pb12 pb20-md">
      <Section.Heading className="dn db-l mb1">
        About {puppy.name}
      </Section.Heading>
      <p>
        {puppy.breed} &bull; {puppy.age_description}
      </p>
      <p className="secondary mt3 mt4-md">
        {puppy.summary_description}&nbsp;
        {puppy.adult && (
          <SmartLink
            to={HELP_CENTER_ADULT_DOGS_PATH}
            className="underline"
            target="_blank"
          >
            Learn more about available adult dogs.
          </SmartLink>
        )}
      </p>
      {puppy.general_attributes.length > 0 && (
        <div className="AboutPuppy__generalAttributes mt6 mt8-md">
          <ul className="row slim-gutters">
            {puppy.general_attributes.map((attr, idx) => {
              return (
                <li key={idx}>
                  <div className="flex flex-column h-100 justify-center bg-subtle-light-gray br3 pv4 ph4 pv5-md">
                    <p className="f2 fw-medium mb1">{attr.title}</p>
                    <p className="secondary">{attr.description}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="mv8 mv12-md">
        <div className="dib relative mb3">
          <UserAvatar
            size={IS_LT_MD ? 'md' : 'xl'}
            user={{ ...breeder, id: breeder.user_id }}
          />
          {breeder.is_preferred_breeder && (
            <PreferredBreederBadge
              className="absolute right-0 bottom-0"
              width={BADGE_DIMS.width}
              height={BADGE_DIMS.height}
              hideTooltip
            />
          )}
        </div>
        <h6 className="heading-6">
          Raised by {breeder.display_name} of {breeder.program_name}
        </h6>
        <ul className="secondary mv2">
          <li className="flex items-center mb2">
            <Icon
              className="mr2"
              height="20px"
              name="fetch-badge-paw"
              width="20px"
            />
            <span>{getBreederDescription()}</span>
          </li>
          {breederResponseDescription && (
            <li className="flex items-center">
              <Icon
                className="mr2"
                height="20px"
                name="fetch-message"
                width="20px"
              />
              <span>{breederResponseDescription}</span>
            </li>
          )}
        </ul>
        {ownerStories.total_count > 0 && (
          <a
            href="#ownerStoriesSection"
            className="dib bg-subtle-gray br-pill f2 stone-700 ph4 pv1"
          >
            {ownerStories.total_count} verified{' '}
            {ownerStories.total_count === 1 ? 'story' : 'stories'}
          </a>
        )}
      </div>
      <div className="dn-ns mv12">
        <h5 className="heading-5">Pricing</h5>
        {puppy.price_description ? (
          <>
            <p className="secondary mt2">{puppy.price_description}</p>
            <PriceBreakdown
              deposit={puppy.deposit}
              description={puppy.price_breakdown_description}
              puppyPayment={puppy.puppy_payment}
              totalPayment={puppy.total_payment}
              analytics={{
                view: ANALYTICS_VIEWS.PUPPY_PROFILE,
                view_feature_location:
                  ANALYTICS_VIEW_FEATURE_LOCATIONS.PUPPY_DETAILS
              }}
            />
            {puppy.bnpl_short_description && (
              <p className="font-14 mt4">
                {puppy.bnpl_short_description}&nbsp;
                <BuyNowPayLaterDetails />
              </p>
            )}
          </>
        ) : (
          <p className="secondary mt2">
            Request info to get more details about {puppy.name}&apos;s price.
          </p>
        )}
      </div>
      <div className="dn-ns mv12">
        <h5 className="heading-5">Getting {puppy.name} home</h5>
        <p className="mt2">{puppy.transportation_description}</p>
      </div>
      <div className="AboutPuppy__whatsIncluded mt8 mt12-md">
        <h5 className="heading-5">What&apos;s included with {puppy.name}</h5>
        <ul
          className={classnames('mt2 mt4-md', {
            'collapsed--mobile': includedWithPuppy.length > 5 && !showFullList,
            'collapsed--desktop': includedWithPuppy.length > 10 && !showFullList
          })}
        >
          {includedWithPuppy.map((item, idx) => {
            const iconName = item.key.includes('fetch')
              ? item.key
              : `extras-${item.key}`;

            return (
              <li
                key={idx}
                className={classnames('flex items-center pv4 bt b--black-10', {
                  bn: idx === 0,
                  'bn-ns': idx === 1
                })}
              >
                <Icon
                  className="mr4"
                  height="32px"
                  name={iconName}
                  width="32px"
                />
                <p>{item.name}</p>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className={classnames(
            'fw-medium br1 stone-700 bg-subtle-gray mt4 ph12 pv3 w-100 tc',
            {
              dn: showFullList || includedWithPuppy.length <= 5,
              'dn-ns': showFullList || includedWithPuppy.length <= 10
            }
          )}
          onClick={() => setShowFullList(prevState => !prevState)}
        >
          View full list
        </button>
      </div>
      <IconSet />
    </div>
  );
}
